import { useEffect, useRef } from 'react'
import { useLocation, useNavigation } from 'react-router'

export const useViewTransition = () => {
  const navigation = useNavigation()
  const location = useLocation()

  const previousPathname = useRef(location.pathname)
  const active = useRef<{
    targetPath: string
    resolve: () => void
    skip: () => void
  } | null>(null)

  useEffect(() => {
    return () => {
      active.current?.resolve()
      active.current?.skip()
      active.current = null
    }
  }, [])

  useEffect(() => {
    if (typeof document === 'undefined') return
    if (!('startViewTransition' in document)) return

    if (active.current && navigation.state !== 'idle' && navigation.location?.pathname !== active.current.targetPath) {
      active.current.resolve()
      active.current.skip()
      active.current = null
    }

    if (navigation.state === 'loading' && !active.current) {
      const targetPath = navigation.location?.pathname
      if (targetPath && targetPath !== previousPathname.current) {
        let resolveUpdate = () => {}
        // Own the resolver before the browser can defer its snapshot callback past idle.
        const update = new Promise<void>((resolve) => {
          resolveUpdate = resolve
        })
        const transition = document.startViewTransition(() => update)
        const current = {
          targetPath,
          resolve: resolveUpdate,
          skip: () => {
            // Our deliberate skip rejects ready; leave unowned native failures observable.
            void transition.ready.catch(() => {})
            transition.skipTransition()
          },
        }
        active.current = current

        const clear = () => {
          current.resolve()
          if (active.current === current) active.current = null
        }
        void transition.finished.then(clear, (error: unknown) => {
          clear()
          // Keep genuine native failures observable without a dangling cleanup promise.
          reportError(error)
        })
      }
    }

    if (navigation.state === 'idle') {
      previousPathname.current = location.pathname
      active.current?.resolve()
    }
  }, [navigation.state, navigation.location, location.pathname])
}
